import {
  AgGridReact,
  type CustomCellRendererProps,
} from '@mint-lib/ag-grid/react';
import {
  ComponentProps,
  memo,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  CellStyleModule,
  ColumnAutoSizeModule,
  CustomFilterModule,
  LocaleModule,
  ModuleRegistry,
  PaginationModule,
  RowStyleModule,
  TextFilterModule,
  ValidationModule,
  type ColDef,
  type GetContextMenuItems,
  type GridApi,
  type IServerSideDatasource,
  type ValueFormatterParams,
} from '@mint-lib/ag-grid/community';
import {
  ColumnApiModule,
  ColumnMenuModule,
  ContextMenuModule,
  FiltersToolPanelModule,
  LicenseManager,
  ServerSideRowModelApiModule,
  ServerSideRowModelModule,
  SetFilterModule,
  SideBarModule,
} from '@mint-lib/ag-grid/enterprise';
import { useTranslation, type TFunction } from '@mint-lib/i18n';
import { AutoImport, Delete, ManualPointer } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useQueryClient } from '@mint-lib/query-client';
import {
  Chip,
  Divider,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import { ProgressCircular } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';

import type {
  GranularityLevel,
  PlatformsByName,
  TableDataFileItem,
} from '../../api/types.js';
import { useLocalizedGranularityLevelLabels } from '../../constants/i18n/granularityLevel.js';
import {
  useImportHubV2,
  useInfiniteScrollFF,
  usePlatformCampaignLevelFF,
} from '../../hooks/ff/index.js';
import {
  useFormattedDate,
  useIsReadOnlyUser,
  useShouldNeverHappen,
} from '../../hooks/index.js';
import { usePerformanceRouteApi } from '../../hooks/useImportHubRouteApi.js';
import {
  useDataFilesFilterOptionsQueryOptions,
  useDataFilesListQueryOptions,
  useDeleteDataFile,
  usePlatformsCatalog,
} from '../../queries/index.js';
import {
  convertFilterModelToURLParams,
  convertSortModelToURLParams,
  createImportsFilterModelFromUrlParams,
  createSortModelFromURLParams,
  transformFilterModelToAPIParams,
  type FilterModel,
} from '../../utils/ag-grid.js';
import {
  checkIsNewItem,
  getDataFileStatusContent,
  getRowClassName,
  isDataFileRemovable,
} from '../../utils/dataFileUtils.jsx';
import { DataFileLink } from '../atoms/DataFileLink.jsx';
import { GridDateRangeInput } from '../atoms/GridDateRangeInput.jsx';
import { GridDateRangePicker } from '../atoms/GridDateRangePicker.jsx';
import { GridEmptyState } from '../atoms/GridEmptyState.jsx';
import { GridMenuItem } from '../atoms/GridMenuItem.jsx';
import { GridShowHiddenFilesFilter } from '../atoms/GridShowHiddenFilesFilter.jsx';
import { RichTooltip } from '../atoms/RichTooltip.jsx';
import { DataFileMoreActions } from './DataFileMoreActions.jsx';
import { ImportDeleteDialog } from './ImportDeleteDialog.jsx';
import PlatformsList from './PlatformsList.jsx';

type AGGridProps = ComponentProps<typeof AgGridReact<TableDataFileItem>>;
type SetFilterValuesParams = {
  success: (values: string[]) => void;
};

ModuleRegistry.registerModules([
  LocaleModule,
  ServerSideRowModelModule,
  RowStyleModule,
  CellStyleModule,
  ColumnApiModule,
  ColumnMenuModule,
  ContextMenuModule,
  PaginationModule,
  ValidationModule,
  ColumnAutoSizeModule,
  ServerSideRowModelApiModule,
  SideBarModule,
  TextFilterModule,
  SetFilterModule,
  CustomFilterModule,
  FiltersToolPanelModule,
]);

if (import.meta.env.VITE_AG_GRID_LICENSE_KEY) {
  LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);
}

const PLACEHOLDER_ROWS_COUNT = 20;

const getPastImportsTableTranslations = (t: TFunction) => ({
  activity: t('Activity'),
  archived: t('Archived'),
  attributionLevel: t('Attribution level'),
  auto: t('Auto'),
  campaign: t('Campaign'),
  draft: t('Draft'),
  failed: t('Failed'),
  fileName: t('File name'),
  importDate: t('Import date'),
  importType: t('Import type'),
  loading: t('Loading'),
  manual: t('Manual'),
  overwritten: t('Overwritten'),
  platforms: t('Platforms'),
  sender: t('Sender'),
  status: t('Status'),
  template: t('Template'),
  uncompleted: t('Uncompleted'),
  uploaded: t('Uploaded'),
});

const getPastImportsDeleteTranslations = (t: TFunction) => ({
  deletionFailed: t('Failed to delete file'),
  deletionInProgress: t('File deletion is in progress'),
  deletionInProgressSubtitle: t(
    'The file has been deleted and the operation is in progress. Completion time may vary, but it will be done as soon as possible.',
  ),
  deletedSuccessfully: (statusLabel: string) =>
    t('The {{statusLabel}} file has been deleted successfully.', {
      statusLabel,
    }),
});

const getPastImportsContextMenuTranslations = (t: TFunction) => ({
  delete: t('Delete'),
  resumeImportProcess: t('Resume import process'),
});

type PastImportsTableProps = {
  ref: Ref<{ onSearchChange: (searchQuery: string) => void } | null>;
};

export const PastImportsTable = ({ ref }: PastImportsTableProps) => {
  const { t } = useTranslation('@myn/import-hub');

  const performanceRoute = usePerformanceRouteApi();
  const routeSearch = performanceRoute.useSearch();
  const {
    search,
    filters: filterParams = {},
    sortBy,
    sortDirection,
    itemsPerPage,
  } = routeSearch;
  const navigate = performanceRoute.useNavigate();

  const isInfiniteScroll = useInfiniteScrollFF();

  const queryClient = useQueryClient();
  const gridRef = useRef<AgGridReact>(null);
  const shouldNeverHappen = useShouldNeverHappen();
  const isImportHubV2 = useImportHubV2();
  const isPlatformCampaignLevelEnabled = usePlatformCampaignLevelFF();
  const getDataFilesListQueryOptions = useDataFilesListQueryOptions();
  const getDataFilesFilterOptionsQueryOptions =
    useDataFilesFilterOptionsQueryOptions();
  const [forcedRenderError, setForcedRenderError] = useState<null | Error>(
    null,
  );

  const setSearchFilter = useCallback((searchQuery: string) => {
    if (!gridRef.current) {
      return;
    }

    gridRef.current.api.setColumnFilterModel('search', {
      field: 'search',
      filterType: 'text',
      filter: searchQuery,
      type: 'contains',
    });
    gridRef.current.api.onFilterChanged();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearchChange: setSearchFilter,
  }));

  const isReadOnlyUser = useIsReadOnlyUser();
  const {
    data: { platformsByName },
  } = usePlatformsCatalog();

  const granularityLevelLabelByType = useLocalizedGranularityLevelLabels();

  const serverSideDataSource: IServerSideDatasource = useMemo(() => {
    return {
      getRows: async (params) => {
        params.api.hideOverlay();

        const { startRow, endRow, sortModel } = params.request;
        const filterModel = params.request.filterModel as FilterModel;
        if (startRow === undefined || endRow === undefined) {
          shouldNeverHappen('Ag-grid: startRow or endRow is not defined');
          return params.fail();
        }

        const itemsPerPage = endRow - startRow;
        const pageNumber = Math.floor(startRow / itemsPerPage) + 1;

        const sortByField = sortModel[0];

        const filters = transformFilterModelToAPIParams(filterModel);

        try {
          const dataFilesListQueryOptions = getDataFilesListQueryOptions({
            params: {
              pageNumber,
              itemsPerPage,
              sortBy: sortByField?.colId,
              sortDirection: sortByField?.sort,
              ...filters,
            },
          });

          const data = await queryClient.fetchQuery({
            ...dataFilesListQueryOptions,
            staleTime: 30 * 1000,
          });

          if (!data.totalItems) {
            params.api.showNoRowsOverlay();
          }

          params.success({
            rowData: data.items,
            rowCount: data.totalItems,
          });
        } catch (error) {
          setForcedRenderError(error);
        }
      },
    };
  }, [shouldNeverHappen, queryClient, getDataFilesListQueryOptions]);

  const [dataFileToDelete, setDataFileToDelete] = useState<{
    dataFile: TableDataFileItem;
  } | null>(null);

  const { mutateAsync: deleteDataFile, isPending: isDeleting } =
    useDeleteDataFile();

  const pastImportsDeleteTranslations = useMemo(
    () => getPastImportsDeleteTranslations(t),
    [t],
  );

  const handleConfirmDelete = useCallback(
    async ({ dataFile }: { dataFile: TableDataFileItem }) => {
      const { id, status } = dataFile;

      gridRef.current?.api.applyServerSideTransaction({
        remove: [dataFile],
      });

      try {
        await deleteDataFile({ id });

        const isUploaded = status === 'uploaded';

        if (isUploaded) {
          enqueueSnackbar({
            variant: 'warning',
            title: pastImportsDeleteTranslations.deletionInProgress,
            subTitle: pastImportsDeleteTranslations.deletionInProgressSubtitle,
            removable: true,
          });
        } else {
          const statusLabel = getDataFileStatusContent(t, dataFile).label;
          enqueueSnackbar({
            variant: 'success',
            title:
              pastImportsDeleteTranslations.deletedSuccessfully(statusLabel),
            removable: true,
          });
        }
      } catch {
        gridRef.current?.api.applyServerSideTransaction({ add: [dataFile] });

        enqueueSnackbar({
          variant: 'error',
          title: pastImportsDeleteTranslations.deletionFailed,
          removable: true,
        });
      } finally {
        setDataFileToDelete(null);
      }
    },
    [deleteDataFile, pastImportsDeleteTranslations, t],
  );

  const handleDeleteDataFileClick = useCallback(
    (dataFile: TableDataFileItem) => {
      if (['archived', 'deleting', 'overwritten'].includes(dataFile.status)) {
        void handleConfirmDelete({ dataFile });
      } else {
        setDataFileToDelete({ dataFile });
      }
    },
    [handleConfirmDelete],
  );

  const defaultColDef = useMemo(
    (): ColDef => ({
      sortable: false,
      resizable: true,
      suppressHeaderMenuButton: true,
      suppressMovable: true,
      cellClass: '!flex !items-center',
    }),
    [],
  );

  const filterOptionsCache = useRef<Record<string, Record<string, string>>>({});

  const getFilterOptions = useCallback(
    (filterType: string) => async (params: SetFilterValuesParams) => {
      const filterOptionsQueryOptions = getDataFilesFilterOptionsQueryOptions({
        params: {
          include: [filterType],
        },
      });

      const { options } = await queryClient.ensureQueryData(
        filterOptionsQueryOptions,
      );

      filterOptionsCache.current[filterType] = Object.fromEntries(
        options.map((opt) => [opt.key, opt.value]),
      );

      params.success(options.map((opt) => opt.key));
    },
    [getDataFilesFilterOptionsQueryOptions, queryClient],
  );

  const getFilterValueFormatter = useCallback(
    (filterType: string) =>
      (params: ValueFormatterParams<TableDataFileItem>) => {
        return filterOptionsCache.current[filterType][params.value];
      },
    [],
  );

  const [initialFilterModel] = useState(() =>
    createImportsFilterModelFromUrlParams({
      search,
      ...filterParams,
    }),
  );

  const [initialSortModel] = useState(() => {
    if (sortBy && sortDirection) {
      return createSortModelFromURLParams(sortBy, sortDirection);
    }
    return [];
  });

  const pastImportsTableTranslations = useMemo(
    () => getPastImportsTableTranslations(t),
    [t],
  );

  const columnDefs = useMemo(() => {
    const attributionLevelFilterValues = [
      'pricing_item',
      'platform',
      'media_row',
      ...(isPlatformCampaignLevelEnabled ? ['platform_campaign'] : []),
    ];

    const columns: Array<ColDef> = [
      {
        field: 'search',
        headerName: '',
        hide: true,
        filter: 'agTextColumnFilter',
        cellDataType: false,
      },
      {
        headerName: pastImportsTableTranslations.fileName,
        field: 'name',
        cellRenderer: FileNameRenderer,
        cellRendererParams: { t },
        minWidth: 300,
      },
      ...(isImportHubV2
        ? [
            {
              headerName: pastImportsTableTranslations.activity,
              field: 'activity',
              cellRenderer: PlainTextRenderer,
              cellRendererParams: { fieldName: 'activityName' },
              minWidth: 300,
              filter: 'agSetColumnFilter',
              filterParams: {
                values: getFilterOptions('activity'),
                valueFormatter: getFilterValueFormatter('activity'),
              },
            },
          ]
        : [
            {
              headerName: pastImportsTableTranslations.campaign,
              field: 'campaign',
              cellRenderer: PlainTextRenderer,
              cellRendererParams: { fieldName: 'campaignName' },
              minWidth: 300,
              filter: 'agSetColumnFilter',
              filterParams: {
                values: getFilterOptions('campaign'),
                valueFormatter: getFilterValueFormatter('campaign'),
              },
            },
          ]),
      {
        field: 'platform',
        headerName: pastImportsTableTranslations.platforms,
        cellRenderer: PlatformsRenderer,
        cellRendererParams: { t, platformsByName },
        minWidth: 300,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: getFilterOptions('platform'),
          valueFormatter: getFilterValueFormatter('platform'),
        },
      },
      {
        field: 'status',
        headerName: pastImportsTableTranslations.status,
        cellRenderer: StatusRenderer,
        cellRendererParams: { t },
        minWidth: 200,
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [
            'uncompleted',
            'loading',
            'uploaded',
            'overwritten',
            'failed',
            'archived',
          ],
          valueFormatter: (params: ValueFormatterParams) => {
            const statusMap: Record<string, string> = {
              archived: pastImportsTableTranslations.archived,
              draft: pastImportsTableTranslations.draft,
              loading: pastImportsTableTranslations.loading,
              failed: pastImportsTableTranslations.failed,
              overwritten: pastImportsTableTranslations.overwritten,
              uncompleted: pastImportsTableTranslations.uncompleted,
              uploaded: pastImportsTableTranslations.uploaded,
            };
            return statusMap[params.value] || params.value;
          },
          suppressSelectAll: false,
          suppressMiniFilter: false,
        },
      },
      {
        headerName: pastImportsTableTranslations.importType,
        field: 'importType',
        cellRenderer: ImportTypeRenderer,
        cellRendererParams: { t },
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['auto', 'manual'],
          valueFormatter: (params: ValueFormatterParams) => {
            return params.value === 'auto'
              ? pastImportsTableTranslations.auto
              : pastImportsTableTranslations.manual;
          },
          suppressSelectAll: false,
          suppressMiniFilter: false,
        },
        minWidth: 140,
      },
      {
        headerName: pastImportsTableTranslations.template,
        field: 'template',
        valueFormatter: (params: ValueFormatterParams) => {
          return params.data?.fromTemplate
            ? params.data?.fromTemplate.name
            : '-';
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          values: getFilterOptions('template'),
          valueFormatter: getFilterValueFormatter('template'),
        },
        minWidth: 300,
      },
      {
        field: 'granularityLevel',
        headerName: pastImportsTableTranslations.attributionLevel,
        valueFormatter: (params: ValueFormatterParams) => {
          return (
            granularityLevelLabelByType[params.value as GranularityLevel] ||
            params.value
          );
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          values: attributionLevelFilterValues,
          valueFormatter: (params: ValueFormatterParams) =>
            granularityLevelLabelByType[params.value as GranularityLevel] ||
            params.value,
        },
        minWidth: 180,
      },
      {
        field: 'sender',
        headerName: pastImportsTableTranslations.sender,
        cellRenderer: PlainTextRenderer,
        cellRendererParams: { fieldName: 'sender' },
        filter: 'agSetColumnFilter',
        filterParams: {
          values: getFilterOptions('sender'),
          valueFormatter: getFilterValueFormatter('sender'),
        },
        minWidth: 200,
      },
      {
        field: 'importDate',
        context: {
          syncWithField: 'importDateFilterPanel',
        },
        headerName: pastImportsTableTranslations.importDate,
        cellRenderer: ImportDateRenderer,
        cellRendererParams: { t },
        filter: { component: GridDateRangePicker },
        suppressHeaderMenuButton: false,
        menuTabs: ['columnsMenuTab'],
        mainMenuItems: (params) => {
          const itemsToExclude = new Set([
            'separator',
            'pinSubMenu',
            'autoSizeAll',
            'autoSizeThis',
            'columnChooser',
            'resetColumns',
          ]);
          return params.defaultItems.filter(
            (item) => !itemsToExclude.has(item),
          );
        },
        suppressFiltersToolPanel: true,
        sortable: true,
        minWidth: 230,
      },
      {
        colId: 'importDateFilterPanel',
        field: 'importDateFilterPanel',
        context: {
          syncWithField: 'importDate',
        },
        headerName: pastImportsTableTranslations.importDate,
        filter: { component: GridDateRangeInput },
        suppressHeaderMenuButton: false,
        hide: true,
      },
      {
        colId: 'includeHidden',
        field: 'includeHidden',
        filter: {
          component: GridShowHiddenFilesFilter,
        },
        filterParams: {
          defaultValue:
            initialFilterModel.includeHidden?.filterType === 'boolean'
              ? initialFilterModel.includeHidden.filter
              : undefined,
        },
        hide: true,
      },
      ...(isReadOnlyUser
        ? []
        : [
            {
              field: 'actions',
              headerName: '',
              cellRenderer: ActionsRenderer,
              cellRendererParams: {
                t,
                handleDeleteDataFileClick,
              },
              pinned: 'right' as const,
              resizable: false,
              width: 40,
              minWidth: 40,
              cellClass: '!flex !justify-center !items-center',
            },
          ]),
    ];

    return columns;
  }, [
    getFilterOptions,
    getFilterValueFormatter,
    granularityLevelLabelByType,
    handleDeleteDataFileClick,
    initialFilterModel,
    isImportHubV2,
    isPlatformCampaignLevelEnabled,
    isReadOnlyUser,
    pastImportsTableTranslations,
    platformsByName,
    t,
  ]);

  const getRowClass: AGGridProps['getRowClass'] = (params) => {
    const { data } = params;

    if (!data) {
      return '';
    }

    const { disabled = false } = getDataFileStatusContent(t, data);

    return getRowClassName({ disabled, createdAt: data.createdDate });
  };

  const onGridReady: AGGridProps['onGridReady'] = useCallback(
    ({ api }: { api: GridApi }) => {
      // Apply initial filter model
      if (Object.keys(initialFilterModel).length > 0) {
        api.setFilterModel(initialFilterModel);
      }

      // Apply initial sort model
      if (initialSortModel.length > 0) {
        api.applyColumnState({
          state: initialSortModel.map((sort) => ({
            colId: sort.colId,
            sort: sort.sort,
          })),
          defaultState: { sort: null },
        });
      }

      // Expand the includeHidden filter in the sidebar
      const filtersToolPanel = api.getToolPanelInstance('filters');
      if (filtersToolPanel) {
        filtersToolPanel.expandFilters(['includeHidden']);
      }
    },
    [initialFilterModel, initialSortModel],
  );

  const onFilterChanged: AGGridProps['onFilterChanged'] = useCallback(
    (params) => {
      if (params.source === 'api') {
        return;
      }

      void (async () => {
        const column = params.columns[0].getColDef();
        const { field, context } = column;

        if (!field) {
          return;
        }

        if (context?.syncWithField) {
          const columnFilterModel = params.api.getColumnFilterModel(field);
          await params.api.setColumnFilterModel(
            context.syncWithField,
            columnFilterModel,
          );
          params.api.onFilterChanged();
        }

        const filterModel = params.api.getFilterModel();
        const filtersParams = convertFilterModelToURLParams(filterModel);

        navigate({
          search: (prev) => ({
            ...prev,
            filters: filtersParams,
          }),
          replace: true,
        });

        params.api.refreshServerSide({ purge: true });
      })();
    },
    [navigate],
  );

  const onSortChanged: AGGridProps['onSortChanged'] = useCallback(
    (params) => {
      const sortModel = params.api
        .getColumnState()
        .filter(
          (col): col is { colId: string; sort: 'asc' | 'desc' } =>
            col.sort === 'asc' || col.sort === 'desc',
        );

      const { sortBy, sortDirection } = convertSortModelToURLParams(sortModel);

      navigate({
        search: (prev) => ({
          ...prev,
          sortBy,
          sortDirection: sortDirection as 'asc' | 'desc' | undefined,
        }),
        replace: true,
      });

      params.api.refreshServerSide({ purge: true });
    },
    [navigate],
  );

  const lastPageSizeRef = useRef(itemsPerPage);
  const onPaginationChanged: AGGridProps['onPaginationChanged'] = useCallback(
    ({ api }: { api: GridApi }) => {
      const newPageSize = api.paginationGetPageSize();
      if (newPageSize === lastPageSizeRef.current) return;
      lastPageSizeRef.current = newPageSize;

      api.setGridOption('cacheBlockSize', newPageSize);

      navigate({
        search: (prev) => ({
          ...prev,
          itemsPerPage: newPageSize,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const pastImportsContextMenuTranslations = useMemo(
    () => getPastImportsContextMenuTranslations(t),
    [t],
  );

  const getContextMenuItems: GetContextMenuItems<TableDataFileItem> =
    useCallback(
      (params) => {
        if (!params.node?.data) {
          return [];
        }

        const { data: dataFile } = params.node;

        const { editable } = getDataFileStatusContent(t, dataFile);
        const isRemovable = isDataFileRemovable(dataFile.status);

        if (isReadOnlyUser) {
          return [];
        }

        return [
          ...(editable
            ? [
                {
                  name: 'resume-import-process',
                  menuItem: GridMenuItem,
                  menuItemParams: {
                    children: (
                      <DataFileLink dataFileId={dataFile.id}>
                        {pastImportsContextMenuTranslations.resumeImportProcess}
                      </DataFileLink>
                    ),
                  },
                },
              ]
            : []),
          ...(editable
            ? [
                {
                  name: 'separator',
                  menuItem: Divider,
                },
              ]
            : []),
          {
            name: 'delete',
            menuItem: GridMenuItem,
            menuItemParams: {
              className: 'fill-text-error text-text-error',
              onClick: () => handleDeleteDataFileClick(dataFile),
              disabled: !isRemovable,
              children: (
                <>
                  <Delete className="h-4 w-4 fill-current" />
                  {pastImportsContextMenuTranslations.delete}
                </>
              ),
            },
          },
        ];
      },
      [
        handleDeleteDataFileClick,
        isReadOnlyUser,
        pastImportsContextMenuTranslations,
        t,
      ],
    );

  if (forcedRenderError) {
    throw forcedRenderError;
  }

  const paginationProps = isInfiniteScroll
    ? {
        cacheBlockSize: 50,
        maxBlocksInCache: 10,
      }
    : {
        pagination: true,
        paginationPageSizeSelector: [10, 20, 50, 100],
        paginationPageSize: itemsPerPage,
        cacheBlockSize: itemsPerPage,
        onPaginationChanged,
      };

  return (
    <>
      <MemoizedAgGrid
        ref={gridRef}
        defaultColDef={defaultColDef}
        columnDefs={columnDefs as ColDef<TableDataFileItem>[]}
        getRowClass={getRowClass}
        serverSideDatasource={serverSideDataSource}
        getContextMenuItems={getContextMenuItems}
        onGridReady={onGridReady}
        onFilterChanged={onFilterChanged}
        onSortChanged={onSortChanged}
        {...paginationProps}
      />
      {!!dataFileToDelete && (
        <ImportDeleteDialog
          dataFile={dataFileToDelete.dataFile}
          onClose={() => setDataFileToDelete(null)}
          onSubmit={() => handleConfirmDelete(dataFileToDelete)}
          isSubmitting={isDeleting}
        />
      )}
    </>
  );
};

const MemoizedAgGrid = memo(
  ({
    ref,
    ...props
  }: AGGridProps & { ref: Ref<AgGridReact<TableDataFileItem>> }) => {
    const { t } = useTranslation('@myn/import-hub');

    return (
      <AgGridReact
        ref={ref}
        className="[&_.ag-filter-toolpanel-group:has(.show-hidden-files-filter)_.ag-group-title-bar]:hidden! [&_.ag-root.ag-unselectable]:select-auto! [&_.ag-tool-panel-wrapper]:min-w-[290px]!"
        loading={false}
        suppressCellFocus
        loadThemeGoogleFonts
        autoSizeStrategy={{ type: 'fitGridWidth' }}
        rowModelType="serverSide"
        serverSideInitialRowCount={PLACEHOLDER_ROWS_COUNT}
        suppressServerSideFullWidthLoadingRow={true}
        getRowId={(params) => params.data.id}
        domLayout="normal"
        sideBar={{
          toolPanels: ['filters'],
          position: 'right',
        }}
        getLocaleText={(params) => {
          if (
            params.key === 'searchOoo' ||
            params.key === 'ariaSearchFilterText'
          ) {
            return t('Search');
          }

          return params.defaultValue;
        }}
        noRowsOverlayComponent={GridEmptyState}
        {...props}
      />
    );
  },
  // The grid is driven imperatively through `gridRef` (filter/sort model, page
  // size, server-side refreshes), so it deliberately ignores prop churn from
  // the parent instead of re-initialising on every render. `columnDefs` is the
  // one exception: it carries `cellRendererParams` that arrive after the first
  // render (e.g. `platformsByName` once the platforms query resolves), and
  // AG Grid only applies prop changes when this component actually re-renders.
  (prevProps, nextProps) => prevProps.columnDefs === nextProps.columnDefs,
);

const FileNameRenderer = ({
  t,
  data,
  value,
}: CustomCellRendererProps<TableDataFileItem> & {
  t: TFunction;
}) => {
  if (!data) {
    return null;
  }

  const { description, toBeArchived } = getDataFileStatusContent(t, data);

  return (
    <>
      {toBeArchived && (
        <RichTooltip description={description} className="max-w-[480px]">
          <span className="mr-1.5 ml-0.5 inline-block size-2 rounded-full bg-warning-main align-middle" />
        </RichTooltip>
      )}
      <TruncateText tooltipClassName="break-all whitespace-normal">
        {value}
      </TruncateText>
    </>
  );
};

const PlainTextRenderer = ({
  fieldName,
  data,
}: CustomCellRendererProps<TableDataFileItem> & { fieldName: string }) => {
  const value = data?.[fieldName as keyof typeof data] as string;
  if (!value) {
    return null;
  }

  return <TruncateText>{value}</TruncateText>;
};

const PlatformsRenderer = ({
  t,
  data,
  platformsByName,
}: CustomCellRendererProps<TableDataFileItem> & {
  t: TFunction;
  platformsByName: PlatformsByName;
}) => {
  if (!data) {
    return null;
  }

  const { platforms = [] } = data;

  if (!platforms.length) {
    return null;
  }

  const { disabled } = getDataFileStatusContent(t, data);

  const platformsList = platforms
    .map((platform: string) => platformsByName[platform])
    .filter(Boolean);

  return <PlatformsList platforms={platformsList} disabled={disabled} />;
};

const StatusRenderer = ({
  t,
  data,
}: CustomCellRendererProps<TableDataFileItem> & {
  t: TFunction;
}) => {
  if (!data) {
    return null;
  }

  const { status } = data;
  const isFileValidating = status === 'validating';

  const { label, description, color, disabled } = getDataFileStatusContent(
    t,
    data,
  );

  return (
    <div className="flex items-center gap-2">
      <RichTooltip description={description} className="max-w-[480px]">
        <div>
          <Chip
            variant="outline"
            size="small"
            color={color}
            disabled={disabled}
          >
            {label}
          </Chip>
        </div>
      </RichTooltip>
      {isFileValidating && (
        <RichTooltip description={t('Checking the file for errors.')}>
          <div className="flex gap-1">
            <ProgressCircular
              size="xsmall"
              indicatorClassNames="data-[color=primary]:text-tertiary-light"
            />
            <span
              className={cn(
                typographyVariants({ variant: 'caption1' }),
                'text-text-secondary',
              )}
            >
              {t('Validating')}
            </span>
          </div>
        </RichTooltip>
      )}
    </div>
  );
};

const ImportTypeRenderer = ({
  t,
  data,
}: CustomCellRendererProps<TableDataFileItem> & {
  t: TFunction;
}) => {
  if (!data) {
    return null;
  }

  const configByImportType = {
    auto: {
      icon: <AutoImport />,
      label: t('Auto'),
    },
    manual: {
      icon: <ManualPointer />,
      label: t('Manual'),
    },
  } as const;

  const config = configByImportType[data.importType];

  return (
    <Chip
      variant="outline"
      size="small"
      color="silver"
      disabled={getDataFileStatusContent(t, data).disabled}
    >
      {config.icon}
      {config.label}
    </Chip>
  );
};

const ImportDateRenderer = ({
  t,
  data,
}: CustomCellRendererProps<TableDataFileItem> & {
  t: TFunction;
}) => {
  const { getFormattedDateWithTime } = useFormattedDate();
  if (!data) {
    return null;
  }

  const { importDate, createdDate } = data;

  return (
    <div className="flex w-full content-between items-center justify-between">
      <div className="flex items-center">
        {getFormattedDateWithTime(importDate)}
      </div>
      {checkIsNewItem(createdDate) && (
        <span
          className={cn(
            typographyVariants({ variant: 'caption1' }),
            'px-2 text-text-secondary uppercase',
          )}
        >
          {t('New')}
        </span>
      )}
    </div>
  );
};

const ActionsRenderer = ({
  t,
  data,
  node,
  handleDeleteDataFileClick,
}: CustomCellRendererProps<TableDataFileItem> & {
  t: TFunction;
  handleDeleteDataFileClick: (dataFile: TableDataFileItem) => void;
}) => {
  if (!data || !node) {
    return null;
  }

  const { editable } = getDataFileStatusContent(t, data);

  return (
    <DataFileMoreActions
      dataFileId={data.id}
      isEditable={Boolean(editable)}
      isRemovable={isDataFileRemovable(data.status)}
      onDeleteClick={() => handleDeleteDataFileClick(data)}
    />
  );
};
